import { UserRoles as Roles } from '@deltasierra/features-auth-core';
import { Brand, createBranded } from '@deltasierra/type-utilities';
import { Agency, AgencyId } from '../agency';
import { PrintUnits } from '../builderDocument';
import { ApiRoute, FullAuditable, HasId, HttpMethod, ObjectOrVoid, Omit, PathBuilder } from '../common';
import { LocationId, LocationIdHierarchy } from '../location';
import { Upload, UploadId } from '../upload';
import { UserJobTitle } from './UserJobTitle';

export { Roles };

export interface BaseUser extends HasId<UserId> {
    firstName: string;
    lastName: string;
    username: string;
    roles: Roles[];
    timezone: string;
    title: string | null;
    localeCode: string;
    printUnits: PrintUnits;
}

export interface User extends BaseUser, FullAuditable {
    agency?: Agency;
    agencyId: AgencyId;
    createdById: UserId | null;
    defaultLocationId: LocationId | null;
    deletedById: UserId | null;
    graphqlAgencyId: string;
    guidedTourEnabled: boolean;
    guidedTourPostponed: boolean;
    hasAcceptedInvite: boolean;
    hashed_pwd?: string;
    hidAssetLibraryPermissionsPopupAt: Date | null;
    lastLoginAt: Date | null;
    lastUpdateSent: Date;
    lavPreferenceOpen: boolean;
    marketingUpdatesEnabled: boolean;
    profilePicture?: Upload;
    profilePictureId: UploadId | null;
    salt?: string;
    specialRequestNotificationsEnabled: boolean;
    jobTitle: UserJobTitle | null;
}

export type SanitizedUser = Omit<User, 'hashed_pwd' | 'salt'>;

export interface UserAccessHierarchy extends LocationIdHierarchy {
    userId: UserId;
    roles: Roles[];
}

export type AssignedUser = BaseUser;

export type BasicUserDto = Pick<User, 'firstName' | 'id' | 'lastName' | 'username'>;

export type AgencyUserDto = Pick<User, 'firstName' | 'id' | 'lastName'>;

export type UpdateGuidedTourBody = Pick<User, 'guidedTourEnabled' | 'guidedTourPostponed'>;

export type UpdateProfileDto = Partial<
    Pick<
        User,
        | 'defaultLocationId'
        | 'firstName'
        | 'guidedTourEnabled'
        | 'guidedTourPostponed'
        | 'hidAssetLibraryPermissionsPopupAt'
        | 'jobTitle'
        | 'lastName'
        | 'lavPreferenceOpen'
        | 'localeCode'
        | 'marketingUpdatesEnabled'
        | 'printUnits'
        | 'profilePictureId'
        | 'specialRequestNotificationsEnabled'
        | 'timezone'
        | 'title'
        | 'username' // Why do we allow users to change their username?
    >
>;

export type UpdateClientUserDto = Partial<
    Pick<
        User,
        | 'defaultLocationId'
        | 'firstName'
        | 'guidedTourEnabled'
        | 'guidedTourPostponed'
        | 'hidAssetLibraryPermissionsPopupAt'
        | 'jobTitle'
        | 'lastName'
        | 'lavPreferenceOpen'
        | 'localeCode'
        | 'marketingUpdatesEnabled'
        | 'profilePictureId'
        | 'roles'
        | 'specialRequestNotificationsEnabled'
        | 'timezone'
        | 'title'
        | 'username' // Why do we allow users to change their username?
    >
>;

export type UserUpdates = UpdateClientUserDto & { password?: string | null };
export type BaseInviteUserDto = Pick<User, 'localeCode' | 'timezone' | 'username'>;
export type InviteClientUserDto = BaseInviteUserDto & Pick<User, 'guidedTourEnabled' | 'roles'>;
export type InviteAgencyUserDto = BaseInviteUserDto;

export function isInviteClientUserDto(value: InviteAgencyUserDto | InviteClientUserDto): value is InviteClientUserDto {
    return 'roles' in value && typeof value.roles !== 'undefined';
}

export type UpdateManagerDto = Partial<Pick<User, 'firstName' | 'lastName' | 'localeCode' | 'timezone' | 'username'>>;

// DS-9786: We need to prevent users with the connect to access role from having any other roles
export const CONNECT_TO_ACCESS_EXCLUSIVE_ROLES = new Set([
    Roles.analyst,
    Roles.builder,
    Roles.planner,
    Roles.publisher,
    Roles.specialRequester,
]);

export type HasRoles = Pick<User, 'roles'>;

/**
 * Return whether or not a user has a given role.
 *
 * @param user - The user to check
 * @param role - The role to check for
 * @returns Whether or not the user has the given role
 */
export function hasRole(user: HasRoles, role: Roles): boolean {
    if (user && user.roles && user.roles.length > 0) {
        return user.roles.filter(userRole => userRole.toString() === role.toString()).length > 0;
    } else {
        return false;
    }
}

/**
 * Check whether the given user has at least one of the provided roles.
 *
 * @param user - The user to check
 * @param roles - The roles to check
 * @returns Whether or not the user has the given role
 */
export function hasAtLeastOneRole(user: HasRoles, ...roles: [Roles, ...Roles[]]): boolean {
    for (const role of roles) {
        if (user.roles.includes(role)) {
            return true;
        }
    }
    return false;
}

/**
 * Check if the given user is site administrator administrator.
 *
 * @param user - The user to check
 * @returns Whether the user is a site admin
 */
export function isAdmin(user: HasRoles): boolean {
    return hasRole(user, Roles.admin);
}

/**
 * Check if the given user has the manager role (ie: check if the user is an Agency user)
 *
 * @param user - The user to check
 * @returns Whether or not the user is an Agency user
 */
export function isManager(user: HasRoles): boolean {
    return hasRole(user, Roles.manager);
}

/**
 * Check if the user has the client role (aka: whether or not the user is a Location-Level user)
 *
 * @param user - The user to check
 * @returns Whether or not the user is a Location-level user
 */
export function isClient(user: HasRoles): boolean {
    return hasRole(user, Roles.client);
}

/**
 * Check if the user has all the required roles to publish
 *
 * @param user - The user to check
 * @returns Whether or not the user has permission to publish
 */
export function canPublish(user: Pick<User, 'roles'>): boolean {
    return hasAtLeastOneRole(user, Roles.admin, Roles.manager, Roles.publisher);
}

/**
 * Check if the user has all the required roles to use the planner and planner related features.
 *
 * @param user - The user to check
 * @returns Whether or not the user has permission
 */
export function canUsePlanner(user: Pick<User, 'roles'>): boolean {
    return hasAtLeastOneRole(user, Roles.admin, Roles.manager, Roles.planner);
}

export const ProfileApi = {
    getMyProfile: new ApiRoute<User, void, void, void>(HttpMethod.Get, path => path.literal('profile').literal('me')),
    updateProfile: new ApiRoute<User, void, UpdateProfileDto, void>(HttpMethod.Patch, path => path.literal('profile')),
};

export interface ClientUserApiParams {
    userId: UserId;
}

const CLIENT_USERS_COLLECTION_API_ROOT = <T extends ObjectOrVoid>(path: PathBuilder<T>) =>
    path.literal('users').literal('clients');
const CLIENT_USER_API_ROOT = <T extends ClientUserApiParams>(path: PathBuilder<T>) =>
    CLIENT_USERS_COLLECTION_API_ROOT(path).param('userId');

export const ClientUserApi = {
    deleteClientUser: new ApiRoute<void, ClientUserApiParams, void, void>(HttpMethod.Delete, CLIENT_USER_API_ROOT),
    getClientUser: new ApiRoute<User, ClientUserApiParams, void, void>(HttpMethod.Get, CLIENT_USER_API_ROOT),
    updateClientUser: new ApiRoute<User, ClientUserApiParams, UpdateClientUserDto, void>(
        HttpMethod.Put,
        CLIENT_USER_API_ROOT,
    ),
};

interface UserLocationsApiParams extends ClientUserApiParams {
    locationId: LocationId;
}
const USER_LOCATIONS_COLLECTION_API_ROOT = <T extends ClientUserApiParams>(path: PathBuilder<T>) =>
    CLIENT_USER_API_ROOT(path).literal('locations');
const USER_LOCATION_API_ROOT = <T extends UserLocationsApiParams>(path: PathBuilder<T>) =>
    USER_LOCATIONS_COLLECTION_API_ROOT(path).param('locationId');
export const UserLocationsApi = {
    addClientUserToLocation: new ApiRoute<void, UserLocationsApiParams, void, void>(
        HttpMethod.Put,
        USER_LOCATION_API_ROOT,
    ),
    removeClientUserFromLocation: new ApiRoute<void, UserLocationsApiParams, void, void>(
        HttpMethod.Delete,
        USER_LOCATION_API_ROOT,
    ),
    setClientUserLocations: new ApiRoute<void, ClientUserApiParams, { locationIds: LocationId[] }, void>(
        HttpMethod.Post,
        USER_LOCATIONS_COLLECTION_API_ROOT,
    ),
};

/**
 * @deprecated
 * Instead use
 * ```
 * import { UserId } from '@deltasierra/ids';
 * ```
 */
export type UserId = Brand<number, 'UserId'>;
export const UserId = createBranded<UserId>();

export interface UserInviteParams {
    token: string;
}

export interface ResendUserInviteBody {
    userId: UserId;
}

export type AcceptUserInviteBody = Pick<User, 'firstName' | 'lastName' | 'localeCode' | 'timezone' | 'title'> & {
    password: string;
};

const acceptUserInviteRoot = (path: PathBuilder<UserInviteParams>) =>
    path.literal('invites').literal('accept').param('token');

const resendUserInvitePath = (path: PathBuilder<void>) => path.literal('invites').literal('resend');

export const UserInviteApi = {
    getInvitedUser: new ApiRoute<SanitizedUser, UserInviteParams, void, void>(HttpMethod.Get, acceptUserInviteRoot),
    resendInvite: new ApiRoute<void, void, ResendUserInviteBody, void>(HttpMethod.Post, resendUserInvitePath),
    submitDetailsAndAcceptInvite: new ApiRoute<SanitizedUser, UserInviteParams, AcceptUserInviteBody, void>(
        HttpMethod.Post,
        acceptUserInviteRoot,
    ),
};
